import React, { useEffect, useMemo } from "react";
import {
  Box,
  Typography,
  Button,
  IconButton,
  Paper,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import EditIcon from "@mui/icons-material/Edit";
import { useNavigate, useParams } from "react-router-dom";
import { useDocs } from "../hooks/useDoc";
import { useIntegration } from "../hooks/useIntegration";
import { format } from "date-fns";
import { useUserMsp } from "../hooks/useUserMsp";
import { isMspOrgCustomer } from "../utils/mspOrgCustomer";
import { renderFormattedContent } from "../utils/htmlUtils";

const DocViewPage = () => {
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    docs,
    selectedDoc,
    fetchDocById,
    isLoading: docsLoading,
    selectedCustomerId,
    setCustomerSelection,
  } = useDocs();
  const {
    customers,
    getCustomers,
    isLoading: customersLoading,
  } = useIntegration();
  const { userMspInfo } = useUserMsp();

  // Filter customers by MSP ID and include 'root' customer (displayed as "All")
  const filteredCustomers = useMemo(() => {
    let arr = (customers || []).filter((c: any) => {
      return (
        c.name?.toLowerCase() === "root" ||
        String(c.msp_id) === String(userMspInfo?.msp_id)
      );
    });
    // Ensure 'All' (root) is first
    arr = [
      ...arr.filter((c: any) => c.name?.toLowerCase() === "root"),
      ...arr.filter((c: any) => c.name?.toLowerCase() !== "root" && !isMspOrgCustomer(c)),
    ];
    return arr;
  }, [customers, userMspInfo?.msp_id]);

  // Find doc - prefer selectedDoc over docs array
  const doc = selectedDoc || docs.find((d: any) => d.id === Number(id));

  // Fetch customers when component mounts
  useEffect(() => {
    getCustomers();
  }, [getCustomers]);

  // Fetch specific doc when mounting
  useEffect(() => {
    if (id) {
      fetchDocById(id);
    }
  }, [id, fetchDocById]);

  // Update Redux state when doc's customer changes
  useEffect(() => {
    if (doc?.customer_id) {
      setCustomerSelection(doc.customer_id.toString());
    }
  }, [doc?.customer_id, setCustomerSelection]);

  // Get customer name
  const customerName = doc?.customer_id
    ? filteredCustomers.find((c: any) => c.id === doc.customer_id)?.name || ""
    : "";

  const isLoading = docsLoading || customersLoading;

  if (isLoading) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          mx: 1,
          my: 1,
          p: { xs: 2, sm: 3, md: 4 },
          background: "#FFF",
          borderRadius: 1,
          fontFamily: "var(--theo-font-sans)",
        }}
      >
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography>Loading...</Typography>
        </Box>
      </Paper>
    );
  }

  if (!doc) {
    return (
      <Paper
        elevation={0}
        sx={{
          width: "100%",
          mx: 1,
          my: 1,
          p: { xs: 2, sm: 3, md: 4 },
          background: "#FFF",
          borderRadius: 1,
          fontFamily: "var(--theo-font-sans)",
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
          <IconButton onClick={() => navigate("/docs", { state: { selectedCustomer: selectedCustomerId } })}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ ml: 1 }}>
            Doc Not Found
          </Typography>
        </Box>
        <Box sx={{ textAlign: "center", py: 4 }}>
          <Typography color="error">
            The requested doc could not be found.
          </Typography>
          <Button
            variant="outlined"
            onClick={() => navigate("/docs", { state: { selectedCustomer: selectedCustomerId } })}
            sx={{ mt: 2 }}
          >
            Back to Docs
          </Button>
        </Box>
      </Paper>
    );
  }

  return (
    <Paper
      elevation={0}
      sx={{
        width: "100%",
        mx: 1,
        my: 1,
        p: { xs: 2, sm: 3, md: 4 },
        background: "#FFF",
        borderRadius: 1,
        fontFamily: "var(--theo-font-sans)",
      }}
    >
      <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 3 }}>
        <Box sx={{ display: "flex", alignItems: "center" }}>
          <IconButton onClick={() => navigate("/docs", { state: { selectedCustomer: selectedCustomerId } })}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h5" sx={{ ml: 1 }}>
            Doc
          </Typography>
        </Box>
        <Button
          variant="outlined"
          startIcon={<EditIcon />}
          onClick={() => navigate(`/docs/${id}/edit`)}
        >
          Edit
        </Button>
      </Box>

      {/* Metadata */}
      <Box sx={{ mb: 3, pb: 2, borderBottom: "1px solid #E0E0E0" }}>
        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 2, mb: 1 }}>
          {doc.created_by && (
            <Box>
              <Typography sx={{ fontSize: 12, color: "#838383", fontWeight: 500, fontFamily: "var(--theo-font-sans)" }}>
                Created By
              </Typography>
              <Typography sx={{ fontSize: 14, fontFamily: "var(--theo-font-sans)" }}>
                {doc.created_by}
              </Typography>
            </Box>
          )}
          {doc.date_added && (
            <Box>
              <Typography sx={{ fontSize: 12, color: "#838383", fontWeight: 500, fontFamily: "var(--theo-font-sans)" }}>
                Created At
              </Typography>
              <Typography sx={{ fontSize: 14, fontFamily: "var(--theo-font-sans)" }}>
                {format(new Date(doc.date_added), "PPP")}
              </Typography>
            </Box>
          )}
          {customerName && (
            <Box>
              <Typography sx={{ fontSize: 12, color: "#838383", fontWeight: 500, fontFamily: "var(--theo-font-sans)" }}>
                Customer
              </Typography>
              <Typography sx={{ fontSize: 14, fontFamily: "var(--theo-font-sans)" }}>
                {customerName.toLowerCase() === "root" ? "All" : customerName}
              </Typography>
            </Box>
          )}
          {doc.last_updated && (
            <Box>
              <Typography sx={{ fontSize: 12, color: "#838383", fontWeight: 500, fontFamily: "var(--theo-font-sans)" }}>
                Last Updated
              </Typography>
              <Typography sx={{ fontSize: 14, fontFamily: "var(--theo-font-sans)" }}>
                {format(new Date(doc.last_updated), "PPP")}
              </Typography>
            </Box>
          )}
        </Box>
      </Box>

      {/* Content */}
      <Box sx={{ mt: 3 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 500, mb: 2, fontFamily: "var(--theo-font-sans)" }}>
          Content
        </Typography>
        <Box
          sx={{
            p: 2,
            borderRadius: 1,
            border: "1px solid #E0E0E0",
            backgroundColor: "#FAFAFA",
            minHeight: 200,
            fontFamily: "var(--theo-font-sans)",
          }}
        >
          {doc.content ? (
            <div
              dangerouslySetInnerHTML={{
                __html: renderFormattedContent(doc.content),
              }}
              style={{ fontFamily: "var(--theo-font-sans)" }}
            />
          ) : (
            <Typography sx={{ color: "#838383", fontStyle: "italic", fontFamily: "var(--theo-font-sans)" }}>
              No content available
            </Typography>
          )}
        </Box>
      </Box>
    </Paper>
  );
};

export default DocViewPage;

